import * as React from 'react';
import {
  ExerciseContainerContentEmpty,
  ExerciseContainer,
} from './ExerciseComponents.style';
import {
  SelectValueProps,
  ExerciseWorkoutProps,
  WorkoutProps,
  WorkoutGroupProps,
} from '../../types';
import {
  ExerciseLibraryModal,
  SelectedExerciseData,
} from './ExerciseLibraryModal';
import {ExercisePreview} from './ExercisePreview';

type CreateExerciseComponentProps = (
  value?: SelectValueProps,
  exerciseData?: SelectedExerciseData,
) => void;

/**
 * ExerciseEmptyComponent Props
 *
 * Migration Notes:
 * - Previously used WorkoutRoutineContext for createWorkoutExerciseBySelected
 * - Now accepts createExerciseComponent as a prop directly
 * - Requirements: 7.3 - Remove legacy context dependencies
 */
type ExerciseEmptyComponentProps = {
  createExerciseComponent: CreateExerciseComponentProps;
  defaultOptions: SelectValueProps[];
  workoutIndex: number;
  workoutGroupIndex: number;
};

/**
 * ExerciseEmptyComponent
 * Legacy component for adding a new exercise
 */
export const ExerciseEmptyComponent = ({
  workoutIndex,
  workoutGroupIndex,
  createExerciseComponent,
  defaultOptions,
}: ExerciseEmptyComponentProps): JSX.Element => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const handleOpenModal = (event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSelectExercise = (exerciseData: SelectedExerciseData) => {
    // Cria o objeto no formato esperado pelo contexto
    const exerciseSelectValue = {
      label: exerciseData.exercise.name,
      value: exerciseData.exercise.id,
      animatedImage: exerciseData.exercise.animatedImage,
      image: exerciseData.exercise.image,
    };

    // Precisa acessar o contexto e workout/workoutGroup diretamente
    // pois createExerciseComponent só aceita o exercício selecionado
    createExerciseComponent(exerciseSelectValue, exerciseData);
    setIsModalOpen(false);
  };

  return (
    <>
      <ExerciseContainer className="add-new">
        <ExerciseContainerContentEmpty
          as="button"
          type="button"
          onClick={handleOpenModal}
          style={{
            border: 'none',
            background: 'none',
            width: '100%',
            cursor: 'pointer',
          }}>
          Adicionar exercício
        </ExerciseContainerContentEmpty>
      </ExerciseContainer>

      <ExerciseLibraryModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSelectExercise={handleSelectExercise}
        workoutGroupIndex={workoutGroupIndex}
        workoutIndex={workoutIndex}
      />
    </>
  );
};
type UpdateExerciseWorkoutProps = {
  (exerciseWorkout: ExerciseWorkoutProps, index: number): void;
};

/**
 * ExerciseComponent Props
 *
 * Migration Notes:
 * - Previously used WorkoutRoutineContext for deleteWorkoutExercise, updateWorkoutExercise
 * - Now accepts these as props directly
 * - Requirements: 7.3 - Remove legacy context dependencies
 */
type ExerciseComponentProps = {
  workoutExercise?: ExerciseWorkoutProps;
  workout: WorkoutProps;
  workoutGroup: WorkoutGroupProps;
  exerciseIndex: number;
  defaultOptions: SelectValueProps[];
  workoutIndex: number;
  workoutGroupIndex: number;
  deleteWorkoutExercise?: (
    exerciseWorkout: any,
    workout: any,
    workoutGroup: any,
  ) => void;
  updateWorkoutExercise?: (
    exerciseWorkout: any,
    index: number,
    workout: any,
    workoutGroup: any,
  ) => void;
};

/**
 * ExerciseComponent
 * Legacy component for displaying and editing an exercise
 */
export const ExerciseComponent = ({
  workoutExercise,
  workoutIndex,
  workoutGroupIndex,
  exerciseIndex,
  defaultOptions,
  workout,
  workoutGroup,
  deleteWorkoutExercise,
  updateWorkoutExercise,
}: ExerciseComponentProps): JSX.Element => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const handleEdit = (event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    setIsModalOpen(true);
  };

  const handleDelete = (event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (deleteWorkoutExercise) {
      deleteWorkoutExercise(workoutExercise, workout, workoutGroup);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleUpdateExercise = (exerciseData: SelectedExerciseData) => {
    const updatedWorkoutExercise = {
      ...workoutExercise,
      exerciseId: exerciseData.exercise.id,
      exercise: {
        label: exerciseData.exercise.name,
        value: exerciseData.exercise.id,
        animatedImage: exerciseData.exercise.animatedImage,
        image: exerciseData.exercise.image,
      },
      series: exerciseData.series || '',
      reps: exerciseData.reps || '',
      weight: exerciseData.weight || '',
      time: exerciseData.time || '',
      restTime: exerciseData.restTime || '',
      notes: exerciseData.notes || '',
      // Keep backward compatibility with old field names
      rounds: exerciseData.series || '',
      repetitions: exerciseData.reps || '',
      load: exerciseData.weight || '',
      timeText: exerciseData.time || '',
    };

    if (updateWorkoutExercise) {
      updateWorkoutExercise(
        updatedWorkoutExercise,
        exerciseIndex,
        workout,
        workoutGroup,
      );
    }
    setIsModalOpen(false);
  };

  const getInitialExerciseData = (): SelectedExerciseData => {
    const exerciseId =
      workoutExercise.exerciseId ||
      workoutExercise.exercise?.value ||
      workoutExercise.exercise?.id ||
      '';
    const exerciseName =
      workoutExercise.exercise?.label || workoutExercise.exercise?.name || '';

    const dataStructure = {
      exerciseId: workoutExercise.exerciseId,
      'exercise.value': workoutExercise.exercise?.value,
      'exercise.id': workoutExercise.exercise?.id,
      'exercise.label': workoutExercise.exercise?.label,
      'exercise.name': workoutExercise.exercise?.name,
    };

    if (!exerciseId || !exerciseName) {
      console.warn(
        '[ExerciseComponent] Exercise data issue detected after save:',
        {
          extractedId: exerciseId,
          extractedName: exerciseName,
          actualDataStructure: dataStructure,
          fullWorkoutExercise: workoutExercise,
        },
      );
    }

    return {
      exercise: {
        id: exerciseId,
        name: exerciseName,
        animatedImage: workoutExercise.exercise?.animatedImage,
        image: workoutExercise.exercise?.image,
      },
      series: workoutExercise.series || workoutExercise.rounds || '',
      reps: workoutExercise.reps || workoutExercise.repetitions || '',
      weight: workoutExercise.weight || workoutExercise.load || '',
      time: workoutExercise.time || workoutExercise.timeText || '',
      restTime: workoutExercise.restTime || '',
      notes: workoutExercise.notes || '',
    };
  };

  return (
    <>
      <ExercisePreview
        workoutExercise={workoutExercise}
        workout={workout}
        workoutGroup={workoutGroup}
        workoutGroupIndex={workoutGroupIndex}
        workoutIndex={workoutIndex}
        exerciseIndex={exerciseIndex}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <ExerciseLibraryModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSelectExercise={handleUpdateExercise}
        workoutGroupIndex={workoutGroupIndex}
        workoutIndex={workoutIndex}
        initialExerciseData={getInitialExerciseData()}
        isEditing={true}
      />
    </>
  );
};
